import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Star, MessageSquare, ChevronLeft, ChevronRight, ThumbsUp } from 'lucide-react';
import { useToast } from '../../hooks/useToast';
import { useDomainQuery } from '../../hooks/useDomainQuery';
import { getPublishedCourseReviews, CourseReview } from '../../services/elearningReviewService';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';

interface CourseReviewsProps {
  courseId: string;
  ratingAverage: number;
  ratingCount: number;
}

const CourseReviews: React.FC<CourseReviewsProps> = ({
  courseId,
  ratingAverage,
  ratingCount
}) => {
  const { getFilterParams } = useDomainQuery();
  const { toast } = useToast();
  
  const [reviews, setReviews] = useState<CourseReview[]>([]);
  const [totalReviews, setTotalReviews] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  const reviewsPerPage = 5;
  const totalPages = Math.ceil(totalReviews / reviewsPerPage);

  useEffect(() => {
    if (courseId) {
      loadReviews();
    }
  }, [courseId, currentPage]);

  const loadReviews = async () => {
    const { tenantId } = getFilterParams();
    if (!tenantId) return;
    
    setIsLoading(true);
    try {
      const offset = (currentPage - 1) * reviewsPerPage;
      const { data, count } = await getPublishedCourseReviews(
        tenantId,
        courseId,
        reviewsPerPage,
        offset
      );
      
      setReviews(data);
      setTotalReviews(count);
    } catch (error) {
      console.error('Erreur lors du chargement des avis:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les avis du cours.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Génère les étoiles pour l'affichage des notes
  const renderStars = (rating: number) => {
    return (
      <div className="flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={16}
            className={`${
              star <= rating ? 'text-yellow-400' : 'text-gray-400'
            }`}
            fill={star <= rating ? 'currentColor' : 'none'}
          />
        ))}
      </div>
    );
  };

  // Génère un résumé des notes (5 étoiles, 4 étoiles, etc.)
  const renderRatingSummary = () => {
    return (
      <div className="mb-6 p-4 bg-gray-800/50 rounded-lg">
        <div className="flex items-center mb-4">
          <div className="mr-4">
            <div className="text-4xl font-bold text-white">{ratingAverage.toFixed(1)}</div>
            <div className="flex mt-1">
              {renderStars(Math.round(ratingAverage))}
            </div>
            <div className="text-sm text-white/60 mt-1">
              {ratingCount} {ratingCount > 1 ? 'avis' : 'avis'}
            </div>
          </div>
          
          <div className="flex-1 ml-4">
            {[5, 4, 3, 2, 1].map((star) => (
              <div key={star} className="flex items-center mb-1">
                <div className="w-10 text-sm text-white/80">{star} ★</div>
                <div className="flex-1 mx-2 h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-yellow-500"
                    style={{ width: `${(reviews.filter(r => Math.round(r.rating) === star).length / reviews.length) * 100}%` }}
                  ></div>
                </div>
                <div className="w-8 text-right text-sm text-white/60">
                  {reviews.filter(r => Math.round(r.rating) === star).length}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mt-8">
      <h3 className="text-2xl font-bold mb-4 flex items-center">
        <MessageSquare className="mr-2 text-red-500" size={24} />
        Avis des étudiants
      </h3>

      {isLoading ? (
        <div className="space-y-6">
          <Skeleton className="h-32 w-full bg-gray-800/50" />
          <Skeleton className="h-32 w-full bg-gray-800/50" />
          <Skeleton className="h-32 w-full bg-gray-800/50" />
        </div>
      ) : reviews.length > 0 ? (
        <>
          {renderRatingSummary()}
          
          <AnimatePresence mode="wait">
            <motion.div
              key={currentPage}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              {reviews.map((review) => (
                <motion.div
                  key={review.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-gray-800/50 rounded-lg p-4"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center">
                      <Avatar className="h-10 w-10 mr-3">
                        <AvatarImage src={review.student?.avatar_url || undefined} alt={review.student?.name} />
                        <AvatarFallback className="bg-gray-700 text-white">
                          {review.student?.name?.charAt(0) || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-white">{review.student?.name}</p>
                        <p className="text-sm text-white/60">
                          {format(new Date(review.created_at), 'dd MMMM yyyy', { locale: fr })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center bg-gray-700/50 px-2 py-1 rounded">
                      {renderStars(review.rating)}
                    </div>
                  </div>
                  
                  {review.review_text && (
                    <p className="text-white/80 mt-2">{review.review_text}</p>
                  )}
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-8">
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="border-white/10 text-white hover:bg-white/5"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                
                <div className="text-sm text-white/70">
                  Page {currentPage} sur {totalPages}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="border-white/10 text-white hover:bg-white/5"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12 bg-gray-800/30 rounded-lg">
          <MessageSquare size={40} className="mx-auto text-white/30 mb-3" />
          <p className="text-lg text-white/70 mb-1">Aucun avis pour ce cours</p>
          <p className="text-sm text-white/50">Soyez le premier à donner votre avis après avoir suivi ce cours.</p>
        </div>
      )}
    </div>
  );
};

export default CourseReviews;
